import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@sunstone/ui';
import { formatDateTime } from '@sunstone/utils';

export type HealthLevel =
  | 'sunstone_partner'
  | 'sunstone_tenant'
  | 'sunstone_deployment'
  | 'sunstone_workspace';

export interface Blip {
  bucket: string;
  status: string;
}

const STATUS_CLASSES: Record<string, string> = {
  green: 'bg-green-500 dark:bg-green-400',
  yellow: 'bg-yellow-500 dark:bg-yellow-300',
  red: 'bg-red-500 dark:bg-red-400',
};

export function BlipsStrip({
  blips,
  isLoading,
  emptyCount = 24,
}: Readonly<{ blips: Blip[]; isLoading?: boolean; emptyCount?: number }>) {
  if (isLoading) return <div className="h-5 w-full animate-pulse rounded bg-muted" />;
  const isEmpty = blips.length === 0;
  const count = isEmpty ? emptyCount : blips.length;
  const gap = count > 100 ? 0 : 2;
  return (
    <TooltipProvider>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${count}, 1fr)`,
          gap,
          width: '100%',
        }}
      >
        {isEmpty
          ? Array.from({ length: emptyCount }, (_, i) => (
              <div key={i} style={{ height: 20, borderRadius: 2 }} className="bg-muted" />
            ))
          : blips.map((blip) => {
              const cls = STATUS_CLASSES[blip.status] ?? 'bg-gray-300 dark:bg-gray-600';
              const label = blip.status.charAt(0).toUpperCase() + blip.status.slice(1);
              return (
                <Tooltip key={blip.bucket}>
                  <TooltipTrigger asChild>
                    <div
                      className={cls}
                      style={{
                        height: 20,
                        borderRadius: 2,
                        cursor: 'default',
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <span className="font-mono text-xs">
                      {formatDateTime(new Date(blip.bucket))}
                    </span>
                    <span className="mx-1 opacity-50">·</span>
                    <span>{label}</span>
                  </TooltipContent>
                </Tooltip>
              );
            })}
      </div>
    </TooltipProvider>
  );
}

export function InlineHealthStrip({
  blips,
  isLoading,
}: Readonly<{ blips: Blip[]; isLoading?: boolean }>) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1 font-medium">Overall Health · Last 24h</p>
      <div className="w-40">
        <BlipsStrip blips={blips} isLoading={isLoading} />
      </div>
    </div>
  );
}
